import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import InputWrapper from '../../common/InputWrapper';
import Select from '../../common/Select';
import styled from '@emotion/styled';
import NumericInput from 'react-numeric-input';

import { getCurrencySymbol, preventSubmittingByEnter } from '../../../helpers';

import { DELIVERY_TYPE_TRANSLATE, DELIVERY_INFO_VISIBILITY } from '../../../config';
import DeliveryOptions from './DeliveryOptions';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const DeliveryForm = styled('form')`
  .react-numeric-input {
    flex: 1 1;
    input {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  .thead-light th {
    font-weight: 600;
    border-bottom-color: rgb(222, 226, 230) !important;
    border-bottom-style: solid !important;
    border-bottom-width: 2px !important;
  }

  .fixed-table-body {
    overflow-x: auto;
  }

  tr .visible-on-hover {
    visibility: hidden;
  }

  tr:hover .visible-on-hover {
    visibility: visible;
  }

  .link {
    cursor: pointer;
    text-decoration: underline;
  }

  .header-wrapper {
    display: flex;
    align-items: center;
    column-gap: 16px;
    margin-bottom: 16px;
    position: relative;

    h4 {
      padding: 0;
      margin: 0;
    }
  }

  .tariffs-showing-mode {
    color: #7e57c2;
    cursor: pointer;
  }

  .hidden {
    display: none;
  }
`;

const DeliveryBlock = (props) => {
  const { values, handleChangeInput, handleSubmit, setChangedDeliveries } = props;

  const { checkoutSettings } = useSelector((state) => state.settings);
  const currencySymbol = getCurrencySymbol(checkoutSettings.currency);

  const [editingTdIndex, setEditingTdIndex] = useState();
  const [showAll, setShowAll] = useState(false);

  const { t } = useAutoTranslation();

  const mapDelivery = (d) => {
    if (d.fbmType === 'WILDBERRIES') {
      return { ...d, courierTitle: 'WILDBERRIES FBW', type: 'B2B' };
    }
    if (d.fbmType === 'OZON') {
      return { ...d, courierTitle: 'OZON FBO', type: 'B2B' };
    }
    return { ...d };
  };

  return (
    <DeliveryForm onKeyDown={preventSubmittingByEnter} onSubmit={handleSubmit}>
      <div className="border-bottom">
        <h4 className="header-title">{t('Доставка')}</h4>
      </div>

      {checkoutSettings.deliveryRegionSettings?.commonSettings ? (
        <DeliveryOptions
          deliveryRegionSettings={values.deliveryRegionSettings}
          setDeliveryRegionSettings={(values) =>
            handleChangeInput(`deliveryRegionSettings`, values)
          }
        />
      ) : (
        <div className="row" style={{ marginTop: '16px' }}>
          <div className="col-6">
            <div className="form-group">
              <InputWrapper
                type="text"
                label={t('Наценка за доставку, в %')}
                id="deliveryPremiumAmount"
                value={values.deliveryPremiumAmount ? values.deliveryPremiumAmount.toFixed(2) : '0'}
                onChange={(valueAsNumber) =>
                  handleChangeInput('deliveryPremiumAmount', valueAsNumber)
                }
                textAppend="%"
                component={NumericInput}
                componentProps={{ precision: 2, style: false }}
              />
              <small className="form-text text-muted">{t('Если не указана, не применяется')}</small>
            </div>
          </div>

          <div className="col-6">
            <div className="form-group">
              <InputWrapper
                type="text"
                label={t('Наценка за доставку, фиксированно')}
                id="deliveryPremiumAmount"
                value={
                  values.deliveryPremiumFixAmount ? values.deliveryPremiumFixAmount.toFixed(2) : '0'
                }
                onChange={(valueAsNumber) =>
                  handleChangeInput('deliveryPremiumFixAmount', valueAsNumber)
                }
                textAppend={currencySymbol}
                component={NumericInput}
                componentProps={{ precision: 2, style: false }}
              />
              <small className="form-text text-muted">{t('Если не указана, не применяется')}</small>
            </div>
          </div>
        </div>
      )}

      <div className="row" style={{ marginTop: '16px' }}>
        <div className="col-12">
          <div className="form-group">
            <InputWrapper
              type="text"
              label={t('Страховка отправлений по умолчанию')}
              id="insurancePercent"
              value={values.insurancePercent ? values.insurancePercent.toFixed(2) : '0'}
              onChange={(valueAsNumber) => handleChangeInput('insurancePercent', valueAsNumber)}
              textAppend="%"
              component={NumericInput}
              componentProps={{ precision: 2, style: false }}
            />
            <small className="form-text text-muted">
              {t('Процент от суммы заказа, на который страхуется отправление')}.{' '}
              {t('0% - без страховки')}, {t('100% - полная страховка')}.{' '}
              {t(
                'Страховка стоит дополнительных денег, поэтому часто её занижают, например, до 50% от стоимости',
              )}
              .
            </small>
          </div>
        </div>
      </div>

      <div className="border-top" style={{ paddingTop: '16px' }}>
        <div className="header-wrapper">
          <h4 className="header-title">{t('Способы доставки')}</h4>
          <div className="tariffs-showing-mode" onClick={() => setShowAll(!showAll)}>
            {showAll ? t('Скрыть неактивные') : t('Отобразить все')}
          </div>
        </div>
        <div className="fixed-table-container">
          <div className="fixed-table-body">
            <table className="table table-centered">
              <thead className="thead-light">
                <tr>
                  <th>{t('Доставка')}</th>
                  <th>{t('Продукт')}</th>
                  <th>
                    {t('Видимость')}
                    <span className="help-tooltip-wrapper">
                      <i className="fe-help-circle" />
                      <span className="help-tooltip">
                        {t(
                          `Выключен – скрыт для всех, Только менеджеры – отображается в личном кабинете менеджерам, Покупатели – отображается при оформлении заказа`,
                        )}
                        .
                      </span>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {values.deliveryInfo.map(mapDelivery)
                  ? values.deliveryInfo.map(mapDelivery).map((delivery, index) => (
                      <tr
                        key={delivery.code}
                        className={showAll || delivery.active === true ? '' : 'hidden'}
                      >
                        <td>
                          <div className="font-weight-bold">{delivery.courierTitle}</div>
                          <div className="text-muted">{DELIVERY_TYPE_TRANSLATE[delivery.type]}</div>
                          <div
                            className="text-muted visible-on-hover"
                            onClick={() => setEditingTdIndex(!(editingTdIndex === index) && index)}
                          >
                            <span className="link">{t('Подробнее')}</span>
                            {` `}
                            {index === editingTdIndex ? (
                              <i className="fe-chevron-down" />
                            ) : (
                              <i className="fe-chevron-right" />
                            )}
                          </div>
                        </td>
                        <td>
                          <div>{delivery.product}</div>
                          <div className="text-muted">{delivery.title}</div>
                          {index === editingTdIndex && (
                            <>
                              {delivery.contractNumber && (
                                <div className="text-muted">
                                  {t('Номер договора')}: {delivery.contractNumber}
                                </div>
                              )}
                              {delivery.deliveryCode && (
                                <div className="text-muted">
                                  {t('Внешний код')}: {delivery.deliveryCode}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                        <td style={{ width: '250px' }}>
                          <Select
                            options={DELIVERY_INFO_VISIBILITY.map((item) => ({
                              value: item.value,
                              label: t(item.label),
                            }))}
                            value={DELIVERY_INFO_VISIBILITY.map((item) => ({
                              value: item.value,
                              label: t(item.label),
                            })).find((o) => o.value === values.deliveryInfo[index].visibility)}
                            onChange={({ value }) => (
                              handleChangeInput(`deliveryInfo.${index}.visibility`, value),
                              setChangedDeliveries((prev) => [
                                ...prev,
                                !prev.includes(delivery.code) && delivery.code,
                              ])
                            )}
                          />
                        </td>
                      </tr>
                    ))
                  : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DeliveryForm>
  );
};

export default DeliveryBlock;
